import { z } from "zod";
import { ValidatorInterface } from "../validator-interface";
import { isEmpty } from "../utils/validator-utils";

export class MaxValueValidator extends ValidatorInterface {
    getZodSchema(baseSchema, options = {}) {
        const maxValue = parseFloat(options.value);
        const labelText = options.labelText || "This field";
        const message = options.message || `${labelText} must be no more than ${maxValue}`;

        if (isNaN(maxValue)) {
            // eslint-disable-next-line no-console
            console.warn("Invalid maxValue provided for MaxValueValidator:", options.value);
            return baseSchema; // Return base schema if maxValue is invalid
        }

        // Apply to number schemas
        if (baseSchema instanceof z.ZodNumber) {
            return baseSchema.max(maxValue, { message });
        }
        // Apply to string schemas that should be converted to numbers
        else if (baseSchema instanceof z.ZodString) {
            // First transform string to number, then apply max constraint
            return baseSchema
                .transform(val => {
                    if (isEmpty(val)) {
                        return val; // Let optional() handle empty values
                    }
                    const parsed = parseFloat(val);
                    return isNaN(parsed) ? val : parsed; // Return original if not a number
                })
                .refine(
                    val => {
                        if (isEmpty(val)) {
                            return true; // Let optional() handle this
                        }
                        const num = typeof val === "number" ? val : parseFloat(val);
                        return !isNaN(num) && num <= maxValue;
                    },
                    { message }
                );
        }
        // Apply to transformed schemas (ZodEffects) - common for number fields
        else if (baseSchema instanceof z.ZodEffects) {
            return baseSchema.refine(
                val => {
                    if (isEmpty(val)) {
                        return true; // Let optional() handle this
                    }
                    const num = typeof val === "number" ? val : parseFloat(val);
                    return !isNaN(num) && num <= maxValue;
                },
                { message }
            );
        }

        // eslint-disable-next-line no-console
        console.warn("MaxValueValidator applied to unsupported schema type. Requires ZodNumber, ZodString, or ZodEffects.");
        return baseSchema; // Return base schema if type is not supported
    }

    getErrorMessage(field, options = {}) {
        const maxValue = parseFloat(options.value);
        const labelText = options.labelText || "This field";
        if (isNaN(maxValue)) {
            return "Invalid maximum value requirement specified.";
        }
        return options.message || `${labelText} must be no more than ${maxValue}`;
    }
}
